import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { 
  HiRefresh, 
  HiTrash, 
  HiChevronDown, 
  HiArchive, 
  HiShieldCheck,
  HiCollection
} from "react-icons/hi";
import Select, { components, DropdownIndicatorProps } from 'react-select';
import { DataTable, PageLayout, ActionButton } from "../../components/modern";
import type { Column } from "../../components/modern";
import { adminApi } from "../../utils/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastNotifications";
import './RecoveryPage.scss';

// --- Types ---
interface DeletedItem {
  id: number;
  displayName: string;
  deletedAt: string;
  deletedBy: string;
}

interface ModelOption {
  value: string;
  label: string;
}

// --- Custom react-select Components ---
const DropdownIndicator = (props: DropdownIndicatorProps<ModelOption, false>) => {
  return (
    <components.DropdownIndicator {...props}>
      <HiChevronDown style={{ fontSize: '1.2rem', color: 'var(--primary-blue)' }} />
    </components.DropdownIndicator>
  );
};

export const RecoveryPage: React.FC = () => {
  const { t, i18n } = useTranslation(['common']);
  const isRTL = i18n.language === 'ar';
  const { hasPermission } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasPermission("View Recovery")) {
      navigate("/");
    }
  }, [hasPermission, navigate]);

  const [models, setModels] = useState<ModelOption[]>([]);
  const [selectedModel, setSelectedModel] = useState<ModelOption | null>(null);
  const [items, setItems] = useState<DeletedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingModels, setFetchingModels] = useState(true);

  const fetchModels = async () => {
    try {
      setFetchingModels(true);
      const response = await adminApi.call('/dashboard/recovery/models', 'GET');
      if (response.success && response.data) {
        setModels((response.data as string[]).map(m => ({ value: m, label: m })));
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    } finally {
      setFetchingModels(false);
    }
  };

  const fetchDeletedItems = async (modelName: string) => {
    if (!modelName) {
      setItems([]);
      return;
    }
    try {
      setLoading(true);
      const response = await adminApi.call(`/dashboard/recovery/${modelName}`, 'GET');
      if (response.success) {
        setItems(response.data || []);
      } else {
        showErrorToast(response.error || "Failed to fetch deleted items");
      }
    } catch (error) {
      showErrorToast("Error fetching deleted items");
    } finally {
      setLoading(false);
    }
  };

  const recoverItem = async (id: number) => {
    if (!selectedModel) return;
    if (!window.confirm(isRTL ? "هل أنت متأكد من استعادة هذا العنصر؟" : "Are you sure you want to recover this item?")) return;
    try {
      const response = await adminApi.call(`/dashboard/recovery/${selectedModel.value}/${id}/recover`, 'POST');
      if (response.success) {
        showSuccessToast(isRTL ? "تم استعادة العنصر بنجاح" : "Item recovered successfully");
        fetchDeletedItems(selectedModel.value);
      } else {
        showErrorToast(response.error || "Failed to recover item");
      }
    } catch (error) {
      showErrorToast("Error recovering item");
    }
  };

  useEffect(() => {
    fetchModels();
  }, []);

  useEffect(() => {
    if (selectedModel) {
      fetchDeletedItems(selectedModel.value);
    } else {
      setItems([]);
    }
  }, [selectedModel]);

  const columns: Column[] = [
    {
      key: 'id',
      title: "ID",
      width: '80px',
    },
    {
      key: 'displayName',
      title: isRTL ? "الاسم / العنوان" : "Name / Title",
      render: (val) => val || "N/A",
      sortable: true,
    },
    {
      key: 'deletedAt',
      title: isRTL ? "تاريخ الحذف" : "Deleted At",
      render: (val) => val ? new Date(val).toLocaleString(isRTL ? 'ar-EG' : 'en-US') : "N/A",
      sortable: true,
    },
    {
      key: 'deletedBy',
      title: isRTL ? "حذف بواسطة" : "Deleted By",
      render: (val) => (
        <span className="user-badge">
          <HiShieldCheck style={{ opacity: 0.6 }} />
          {val || (isRTL ? "النظام" : "System")}
        </span>
      )
    },
    {
      key: 'actions',
      title: isRTL ? "العمليات" : "Actions",
      width: '100px',
      align: 'center',
      render: (_, item) => (
        <div className="action-buttons">
          <ActionButton
            variant="success"
            size="sm"
            icon={HiRefresh}
            onClick={() => recoverItem(item.id)}
            tooltip={isRTL ? "استعادة" : "Recover"}
          />
        </div>
      )
    }
  ];

  const breadcrumbs = [
    { label: isRTL ? "الإعدادات" : "Settings", url: "/config" },
    { label: isRTL ? "استعادة المحذوفات" : "Data Recovery Hub" }
  ];

  const selectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      height: '42px',
      minHeight: '42px',
      borderRadius: '10px',
      borderColor: state.isFocused ? 'var(--primary-blue)' : 'var(--border-color)',
      backgroundColor: 'var(--card-bg)',
      boxShadow: state.isFocused ? '0 0 0 4px rgba(74, 95, 231, 0.1)' : 'none',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      '&:hover': {
        borderColor: 'var(--primary-blue)'
      }
    }),
    singleValue: (base: any) => ({
      ...base,
      color: 'var(--text-primary)',
      fontSize: '14px',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }),
    placeholder: (base: any) => ({
      ...base,
      fontSize: '13px',
      color: 'var(--text-muted)',
      fontWeight: '500'
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: '12px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      backgroundColor: 'white', // Solid white for visibility
      padding: '8px',
      marginTop: '8px',
      zIndex: 9999 // Ensure it's on top of everything
    }),
    menuList: (base: any) => ({
      ...base,
      backgroundColor: 'white', // Solid white list
      borderRadius: '10px'
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected ? 'rgba(74, 95, 231, 0.15)' : state.isFocused ? 'rgba(74, 95, 231, 0.05)' : 'transparent',
      color: state.isSelected ? 'var(--primary-blue)' : '#000000', // Black text for options
      fontSize: '13px',
      fontWeight: state.isSelected ? '700' : '500',
      borderRadius: '6px',
      marginBottom: '2px',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: 'rgba(74, 95, 231, 0.2)',
        color: 'var(--primary-blue)'
      }
    })
  };

  if (!hasPermission("View Recovery")) {
    return null;
  }

  return (
    <PageLayout
      title={isRTL ? "مركز استعادة البيانات" : "Unified Data Recovery Hub"}
      subtitle={isRTL ? "استعادة العناصر المحذوفة من النظام لجميع الفئات عبر لوحة مركزية" : "Centralized command center for restoring system-wide deleted records"}
      breadcrumbs={breadcrumbs}
      className={`recovery-page ${isRTL ? 'rtl' : 'ltr'}`}
    >
      <div className="recovery-controls">
        <div className="model-selector-wrapper">
          <label htmlFor="model-select">
            <HiCollection style={{ fontSize: '1.2rem', color: 'var(--primary-blue)' }} />
            {isRTL ? "اختر فئة البيانات:" : "Browse Category:"}
          </label>
          <div style={{ flex: 1 }}>
            <Select
              id="model-select"
              options={models}
              value={selectedModel}
              onChange={(option: any) => setSelectedModel(option)}
              placeholder={fetchingModels ? (isRTL ? "تحليل النظام..." : "Mapping...") : (isRTL ? "-- اختر النوع --" : "-- Select Model --")}
              isLoading={fetchingModels}
              isRtl={isRTL}
              styles={selectStyles}
              components={{ DropdownIndicator }}
              classNamePrefix="react-select"
            />
          </div>
        </div>

        {selectedModel && (
          <div className="stats-badge">
            <div className="pulse-icon">
              <HiArchive />
            </div>
            {isRTL ? `تم العثور على ${items.length} محذوفات` : `Found ${items.length} Archives`}
          </div>
        )}
      </div>

      <div className="recovery-content">
        {selectedModel ? (
          <DataTable
            columns={columns}
            data={items}
            loading={loading}
            searchable={true}
            refreshable={true}
            onRefresh={() => fetchDeletedItems(selectedModel.value)}
            emptyMessage={isRTL ? "لا توجد عناصر مؤرشفة حالياً" : "The digital archive is currently empty"}
          />
        ) : (
          <div className="empty-state">
            <div className="empty-icon-wrapper">
              <HiTrash />
            </div>
            <h3>{isRTL ? "جاهز للاستكشاف" : "Data Archive Ready"}</h3>
            <p>{isRTL ? "يرجى اختيار فئة من الأعلى للبحث في المحذوفات المؤرشفة واستعادتها للنظام." : "Please select a data source from the selector above to browse and restore system archives."}</p>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
